// スキルの網（試作・スズネ）：一直線の系統ではなく、中心から網の目に広がる道。取れるのは、取ったマス（最初は中心）と線でつながったマスだけ。
// 座標は格子（右が x、下が y、中心が 0,0）。線は直角に折れて描く。奥義（jougi）は網のつながりに加えて、術を覚えるスキルも要る
import type { CharId } from '../types';

export const GRAPH_CORE = '@core';
export interface SkillGraph {
  pos: Record<string, [number, number]>;
  edges: [string, string][];
}

/** 記号 → スキルID */
const K: Record<string, string> = {
  CO: GRAPH_CORE,
  ...Object.fromEntries([1, 2, 3, 4, 5].flatMap((i) => [[`H${i}`, `c_hp${i}`], [`A${i}`, `c_atk${i}`], [`D${i}`, `c_def${i}`]])),
  ...Object.fromEntries([1, 2, 3, 4].flatMap((i) => [[`G${i}`, `c_agi${i}`], [`C${i}`, `c_core${i}`], [`N${i}`, `c_neutral${i}`], [`Z${i}`, `c_bp${i}`], [`J${i}`, `c_bhp${i}`], [`S${i}`, `c_mat${i}`]])),
  ...Object.fromEntries([1, 2, 3].flatMap((i) => [[`B${i}`, `c_boss${i}`], [`U${i}`, `c_guard${i}`], [`P${i}`, `c_potion${i}`], [`T${i}`, `c_terr${i}`], [`K${i}`, `c_bpow${i}`]])),
  M1: 'c_bmax1', M2: 'c_bmax2', RS: 'c_respawn1',
};

/** 記号, x, y, つながる先（記号） */
type Row = [string, number, number, string[]];
function build(prefix: string, local: Record<string, string>, rows: Row[]): SkillGraph {
  const id = (k: string) => local[k] ? `${prefix}_${local[k]}` : (K[k] ?? k);
  const pos: SkillGraph['pos'] = {};
  const edges: SkillGraph['edges'] = [];
  for (const [k, x, y, to] of rows) {
    pos[id(k)] = [x, y];
    for (const t of to) edges.push([id(t), id(k)]);
  }
  return { pos, edges };
}

const SUZUNE = build(
  'suzune',
  {
    ko: 'kokoroe', zu: 'zumen', da: 'daiku', ha: 'hayadate',
    kj: 'kanabojutsu', kb: 'kanabo', sk: 'shirokuzushi', og: 'ogui',
    on: 'onihada', fd: 'fudo', dd: 'dandori', tr: 'toryo',
    hz: 'hayazame', of: 'ofushin', tg: 'tegiwa', ks: 'kishin',
    O1: 'ougi', O2: 'ougi_toppu', O3: 'ougi_shuzen',
  },
  [
    ['CO', 0, 0, []],
    // 上：攻城（本陣・大物）。左に妖祓い、右に大物狩り。頂点に奥義
    ['kj', 0, -2, ['CO']],
    ['C1', 0, -4, ['kj']],
    ['kb', 0, -6, ['C1']],
    ['C2', 0, -8, ['kb']],
    ['O1', 0, -10, ['C2', 'sk']],
    ['N1', -2, -2, ['kj']],
    ['N2', -4, -2, ['N1']],
    ['N3', -4, -4, ['N2']],
    ['N4', -2, -6, ['N3', 'kb']],
    ['sk', -2, -8, ['N4']],
    ['B1', 2, -6, ['kb']],
    ['B2', 4, -6, ['B1']],
    ['B3', 6, -6, ['B2']],
    ['og', 4, -8, ['B2']],
    ['C3', 2, -8, ['B1']],
    ['C4', 2, -10, ['C3']],
    // 左：普請（図面・頑丈・工夫・縄張り・段取り）
    ['ko', -2, 0, ['CO']],
    ['Z1', -4, 0, ['ko']],
    ['zu', -4, 2, ['Z1', 'ko']],
    ['J1', -2, 2, ['ko']],
    ['J2', -2, 4, ['J1']],
    ['J3', -4, 4, ['J2', 'zu']],
    ['J4', -6, 4, ['J3']],
    ['M1', -4, 6, ['J2']],
    ['M2', -6, 6, ['M1']],
    ['Z2', -6, 0, ['Z1']],
    ['Z3', -8, 0, ['Z2']],
    ['Z4', -10, 0, ['Z3']],
    ['ha', -10, 2, ['Z3']],
    ['da', -6, 2, ['Z2', 'zu']],
    ['O2', -8, 2, ['da']],
    ['K1', -6, -2, ['Z2', 'Z1']],
    ['K2', -8, -2, ['K1']],
    ['K3', -10, -2, ['K2']],
    ['S1', -6, -4, ['K1']],
    ['S2', -8, -4, ['S1']],
    ['S3', -8, -6, ['S2']],
    ['S4', -6, -6, ['S3', 'N3']],
    // 右：守り（鉄壁・受け流し・薬師・鍛錬・建物で守る）
    ['on', 2, 0, ['CO']],
    ['D1', 4, 0, ['on']],
    ['D2', 6, 0, ['D1']],
    ['D3', 8, 0, ['D2']],
    ['D4', 10, 0, ['D3']],
    ['D5', 12, 0, ['D4']],
    ['fd', 6, 2, ['D2']],
    ['O3', 6, 4, ['fd']],
    ['dd', 8, 2, ['fd', 'D3']],
    ['tr', 10, 2, ['dd']],
    ['U1', 6, -2, ['D2']],
    ['U2', 8, -2, ['U1']],
    ['U3', 10, -2, ['U2']],
    ['RS', 8, -4, ['U2']],
    ['P1', 4, -2, ['D1']],
    ['P2', 4, -4, ['P1', 'B2']],
    ['P3', 6, -4, ['P2']],
    // 下：覚醒と身体（鍛錬・剛力・身軽・陣取り）。左下で普請の頑丈と、右下で守りの鍛錬と合流
    ['H1', 0, 2, ['CO']],
    ['A1', 2, 2, ['H1', 'on']],
    ['H2', 2, 4, ['A1']],
    ['H3', 4, 4, ['H2']],
    ['H4', 4, 6, ['H3']],
    ['H5', 6, 6, ['H4']],
    ['A2', 0, 4, ['H1']],
    ['hz', 0, 6, ['A2']],
    ['of', -2, 6, ['hz', 'J2']],
    ['A3', 2, 6, ['hz']],
    ['A4', 2, 8, ['A3']],
    ['A5', 2, 10, ['A4']],
    ['tg', 0, 8, ['hz']],
    ['ks', 0, 11, ['tg', 'A5']],
    ['G1', -2, 8, ['tg']],
    ['G2', -4, 8, ['G1']],
    ['G3', -4, 10, ['G2']],
    ['G4', -2, 10, ['G3']],
    ['T1', 4, 8, ['A4', 'H4']],
    ['T2', 6, 8, ['T1']],
    ['T3', 6, 10, ['T2']],
  ],
);

// スズネは段（skill-ladder）を試すので網は外す（網の配置は残しておく）
void SUZUNE;
export const SKILL_GRAPHS: Partial<Record<CharId, SkillGraph>> = {};
export const skillGraph = (c: CharId): SkillGraph | undefined => SKILL_GRAPHS[c];

const nbCache = new Map<CharId, Map<string, string[]>>();
/** 隣り合うマス */
export function graphNeighbors(c: CharId, id: string): string[] {
  const g = SKILL_GRAPHS[c];
  if (!g) return [];
  let m = nbCache.get(c);
  if (!m) {
    m = new Map();
    for (const [a, b] of g.edges) {
      (m.get(a) ?? m.set(a, []).get(a)!).push(b);
      (m.get(b) ?? m.set(b, []).get(b)!).push(a);
    }
    nbCache.set(c, m);
  }
  return m.get(id) ?? [];
}
/** 中心から、取ったマスだけを通って届くマス */
export function graphReach(c: CharId, has: ReadonlySet<string>): Set<string> {
  const out = new Set<string>();
  const q = [GRAPH_CORE];
  while (q.length) {
    const x = q.pop()!;
    for (const n of graphNeighbors(c, x)) if (has.has(n) && !out.has(n)) (out.add(n), q.push(n));
  }
  return out;
}
